import { AnnualSurveySchema, OnboardingSurveySchema, QldSurveySchema, type SurveyResponse } from "@qendo/survey";
import { useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { Button } from "~/components/Button";
import { AnnualSurvey2025 } from "~/components/form/survey/AnnualSurvey2025";
import { OnboardingSurvey } from "~/components/form/survey/OnboardingSurvey";
import { QldSurvey } from "~/components/form/survey/QldSurvey";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Dismiss } from "~/components/permission/Dismiss";

import { Space } from "~/constants/sizing";
import type { AnnualSurvey2025Data } from "~/entities/survey/annual-survey2025-data";
import type { OnboardingSurveyData } from "~/entities/survey/onboarding-survey-data";
import type { QldSurveyData } from "~/entities/survey/QldSurveyData";
import { useDismiss } from "~/providers/DismissProvider";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { type ProfileUpdatePayload, useProfile } from "~/providers/ProfileProvider";
import { useSurvey } from "~/providers/SurveyProvider";

export default function Survey() {
    const { id } = useLocalSearchParams<{ id: string }>();
    const errorBag = useErrorBag();
    const { is_primary, updateProfile } = useProfile();
    const { handleDismiss } = useDismiss();
    const { findSurvey, respond } = useSurvey();
    const [response, setResponse] = useState<SurveyResponse | null>(null);
    const { saving, setSaving } = useFeedback();
    const survey = findSurvey(id);

    // biome-ignore lint/correctness/useExhaustiveDependencies(errorBag.empty): Clear errors
    useEffect(() => errorBag.empty(), []);

    const can_save = Boolean(survey && response && response.valid());

    const doSave = async () => {
        if (!response?.valid() || !survey || saving) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        if (!response || !survey) {
            setSaving(false);
            return;
        }
        errorBag.empty();

        respond({ id, response })
            .then(async () => {
                if (is_primary) {
                    const update =
                        (survey.schema === OnboardingSurveySchema &&
                            (response as OnboardingSurveyData).only(["age_range", "gender", "postcode"])) ||
                        (survey.schema === AnnualSurveySchema &&
                            (response as AnnualSurvey2025Data).only(["postcode"])) ||
                        null;

                    if (update) {
                        await updateProfile(update as ProfileUpdatePayload);
                    }
                }
            })
            .then(() => {
                handleDismiss();
            })
            .finally(() => {
                setSaving(false);
            });
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    if (!survey) {
        return <Dismiss />;
    }

    return (
        <FullPageModal
            cancelable
            headerRight={() => (
                <Button title="Save" size="sm" onPress={doSave} disabled={!can_save || is_saving} loading={is_saving} />
            )}
            onRequestClose={handleDismiss}
            gap={Space.sm}
            title={survey.title}
            asRoute
        >
            {survey.schema === AnnualSurveySchema && (
                <>
                    <SectionHeader title="Tell us more about you" icon="heart" />
                    <AnnualSurvey2025
                        value={response as AnnualSurvey2025Data | null}
                        setPayload={setResponse}
                        disabled={is_saving}
                    />
                </>
            )}
            {survey.schema === OnboardingSurveySchema && (
                <>
                    <SectionHeader title="We’re here to support you" icon="heart">
                        To personalise your QENDO app and check in on you from time to time, we’d love to understand
                        where you’re at right now. This is a quick check-in.
                    </SectionHeader>
                    <OnboardingSurvey
                        value={response as OnboardingSurveyData | null}
                        setPayload={setResponse}
                        disabled={is_saving}
                    />
                </>
            )}
            {survey.schema === QldSurveySchema && (
                <QldSurvey value={response as QldSurveyData | null} setPayload={setResponse} disabled={is_saving} />
            )}
        </FullPageModal>
    );
}
